import { atom, Getter } from 'jotai';

import {
  DisplayRef,
  EBIcon,
  IconConfig,
  IconStateConfig,
  Namespace,
  RelatedDevice,
  RelatedDeviceSection,
  Setting,
} from '@repo/sdk/models/components';

import { getActiveState } from '@/features/equipment-browser/conditional-evaluator/get-active-state';
import { EnhancedReading } from '@/features/equipment-browser/subscriptions/use-get-readings-values';
import { EnhancedSetting } from '@/features/equipment-browser/subscriptions/use-get-settings-values';
import { readingsFamily } from '@/hooks/query-hooks/streams/gaia-api/use-readings-stream';
import { settingsFamily } from '@/hooks/query-hooks/streams/gaia-api/use-settings-stream';
import { LatestValue } from '@/types/reading-types';

import { buildEnhancedReading } from './display-metrics.state';
import { buildEnhancedSetting } from './display-settings.state';
import { equipmentResponseAtom } from './equipment-response.state';

export type SubcomponentSectionConfig = {
  title?: string;
  subcomponents: SubcomponentConfig[];
};

export type SubcomponentConfig = {
  displayName: string;
  icon: IconConfig;
  attributes: SubcomponentAttributeConfig[];
};

export type SubcomponentAttributeConfig = {
  type: 'setting' | 'reading';
  setting?: EnhancedSetting;
  reading?: EnhancedReading;
};

/**
 * Atom that provides the related devices from the equipment response
 *
 * @remarks
 * Derives related devices from the equipment response atom. Returns undefined if no equipment
 * response is available or if the equipment has no related devices.
 */
export const relatedDevicesAtom = atom<RelatedDeviceSection[] | undefined>((get) => {
  const equipment = get(equipmentResponseAtom);

  return equipment?.relatedDevices;
});
relatedDevicesAtom.debugLabel = 'relatedDevicesAtom';

// Helper function to get the icon config for a subcomponent based on the active state
const getSubcomponentIconConfig = (icon: EBIcon): IconConfig | undefined => {
  const activeIconState = getActiveState<IconStateConfig>({
    states: icon.states,
  });
  if (activeIconState && activeIconState.config) {
    return activeIconState.config;
  }
  return undefined;
};

// Helper function to get the attribute for a subcomponent based on the namespace
const getSubcomponentAttribute = (
  get: Getter,
  attribute: DisplayRef
): SubcomponentAttributeConfig | undefined => {
  if (attribute.ref?.namespace === Namespace.Setting) {
    if (!attribute.ref?.id) return undefined;
    const setting = get(settingsFamily({ settingId: attribute.ref.id } as Setting));
    if (!setting || !setting.settingId) return undefined;
    return {
      type: 'setting',
      setting: buildEnhancedSetting(attribute, setting as Setting),
      reading: undefined,
    };
  }
  if (!attribute.ref?.id) return undefined;
  const reading = get(readingsFamily({ pointId: attribute.ref.id } as LatestValue));
  // Check if reading is valid - must have timestamp and kind to be a real LatestValue from stream
  // (not just the initial placeholder object)
  if (!reading) return undefined;
  return {
    type: 'reading',
    reading: buildEnhancedReading(attribute, reading as LatestValue),
    setting: undefined,
  };
};

const buildSubcomponentConfig = (
  get: Getter,
  device: RelatedDevice
): SubcomponentConfig | undefined => {
  const icon = getSubcomponentIconConfig(device.icon);
  if (!icon) return undefined;
  const attributes = device.attributes
    .map((attribute) => getSubcomponentAttribute(get, attribute))
    .filter((attribute) => attribute !== undefined);

  return {
    displayName: device.displayName,
    icon: icon,
    attributes: attributes,
  };
};

/**
 * subcomponentSectionConfigsAtom
 * maintains the configuration for the subcomponent sections
 *
 * @remarks
 * Gets the subcomponent section configuration from the related devices atom
 */
export const subcomponentSectionConfigsAtom = atom<SubcomponentSectionConfig[] | undefined>(
  (get) => {
    const relatedDevices = get(relatedDevicesAtom);
    if (!relatedDevices) return undefined;

    const sectionConfigs: SubcomponentSectionConfig[] = [];
    relatedDevices.forEach((section) => {
      const subcomponents = section.devices
        .map((device) => buildSubcomponentConfig(get, device))
        .filter((subcomponent) => subcomponent !== undefined);
      if (subcomponents.length > 0) {
        const sectionConfig: SubcomponentSectionConfig = {
          title: section.title,
          subcomponents: subcomponents,
        };
        sectionConfigs.push(sectionConfig);
      }
    });
    return sectionConfigs;
  }
);

subcomponentSectionConfigsAtom.debugLabel = 'subcomponentSectionConfigsAtom';
